import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Dropdown, Form, InputGroup, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { permissionsService } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import {
  DocumentWorkflowTaskListParams,
  documentWorkflowTaskService,
  GroupedByDocWfRow,
  ResolvedTaskDetail,
} from '../../services/document-workflow-task.service'
import { formatDateTime } from '../../utils/dateFormatter'
import { AppTable } from '../common/AppTable'
import PageHeader from '../common/PageHeader'
import { Pagination } from '../common/Pagination'
import {
  type WorkflowTaskListColumnId,
  WORKFLOW_TASK_LIST_COLUMNS,
  loadWorkflowTaskListColumns,
  saveWorkflowTaskListColumns,
} from './workflowTaskListColumnPrefs'
import './DocumentWorkflowTaskList.scss'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function rowKey(row: GroupedByDocWfRow): string {
  return `${row.document_id}-${row.workflow_definition_id}`
}

export const DocumentWorkflowTaskList: React.FC = () => {
  const { t } = useTranslation()
  const [rows, setRows] = useState<GroupedByDocWfRow[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [count, setCount] = useState(0)
  const [workflowDefinitions, setWorkflowDefinitions] = useState<{ id: number; name: string }[]>([])
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [workflowFilter, setWorkflowFilter] = useState<number | ''>('')
  const [searchTitle, setSearchTitle] = useState('')
  const [searchApplied, setSearchApplied] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [visibleColumns, setVisibleColumns] = useState<WorkflowTaskListColumnId[]>(loadWorkflowTaskListColumns)
  const colShow = useMemo(() => new Set(visibleColumns), [visibleColumns])
  const tableColSpan = 1 + visibleColumns.length
  const lastColumnsContextRef = useRef<string>('')

  useEffect(() => {
    const apply = () => {
      const uid = permissionsService.getCurrentUser()?.id ?? null
      const oid = settingsService.getCurrentOrganizationId()
      const k = `${uid ?? 'x'}:${oid ?? 'x'}`
      if (lastColumnsContextRef.current === k) {
        return
      }
      lastColumnsContextRef.current = k
      setVisibleColumns(loadWorkflowTaskListColumns(oid, uid))
    }
    apply()
    return settingsService.subscribe(apply)
  }, [])

  useEffect(() => {
    documentWorkflowTaskService
      .getWorkflowDefinitionsForFilter()
      .then((res) => setWorkflowDefinitions(res.results ?? []))
      .catch(() => setWorkflowDefinitions([]))
  }, [])

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const params: DocumentWorkflowTaskListParams = {
        page,
        page_size: pageSize,
        status: (statusFilter as DocumentWorkflowTaskListParams['status']) || undefined,
        workflow_definition: workflowFilter === '' ? undefined : Number(workflowFilter),
        document_title: searchApplied || undefined,
      }
      const data = await documentWorkflowTaskService.getGroupedTasksByDocWf(params)
      setRows(data.results ?? [])
      setCount(data.count ?? 0)
    } catch {
      setRows([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, statusFilter, workflowFilter, searchApplied])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const applyFilters = () => {
    setSearchApplied(searchTitle.trim())
    setPage(1)
  }

  const clearFilters = () => {
    setStatusFilter('')
    setWorkflowFilter('')
    setSearchTitle('')
    setSearchApplied('')
    setPage(1)
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value))
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  const from = count === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, count)
  const hasActiveFilters = !!statusFilter || workflowFilter !== '' || !!searchApplied

  const statusBadgeVariant = (status: ResolvedTaskDetail['status']) => {
    switch (status) {
      case 'open':
        return 'primary'
      case 'resolved':
        return 'success'
      case 'cancelled':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const columnLabel = (id: WorkflowTaskListColumnId): string => {
    const keys: Record<WorkflowTaskListColumnId, string> = {
      document: 'document_workflow.document',
      workflow: 'document_workflow.workflow_name',
      workflow_status: 'document_workflow.workflow_status',
      step: 'document_workflow.step',
      status: 'document_workflow.task_status',
      assigned: 'document_workflow.assigned_to',
      created: 'document_workflow.task_created',
    }
    return t(keys[id])
  }

  const toggleColumn = (id: WorkflowTaskListColumnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) {
          return prev
        }
        const next = prev.filter((c) => c !== id)
        saveWorkflowTaskListColumns(next)
        return next
      }
      const next = WORKFLOW_TASK_LIST_COLUMNS.filter((c) => c === id || prev.includes(c))
      saveWorkflowTaskListColumns(next)
      return next
    })
  }

  const statusLabelKey = (status: ResolvedTaskDetail['status']) => {
    switch (status) {
      case 'open':
        return 'document_workflow.task_status_open'
      case 'resolved':
        return 'document_workflow.task_status_resolved'
      case 'cancelled':
        return 'document_workflow.task_status_cancelled'
      default:
        return status
    }
  }

  return (
    <div className="document-workflow-task-list">
      <PageHeader title={t('document_workflow.my_workflow_tasks')} info={t('document_workflow.my_workflow_tasks_info')} />

      <div className="document-workflow-task-list__filters mb-3">
        <div className="row g-2 align-items-end flex-wrap">
          <div className="col-auto">
            <Form.Label className="small mb-0">{t('document_workflow.task_status')}</Form.Label>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              style={{ minWidth: 120 }}
            >
              <option value="">{t('document_workflow.filter_all')}</option>
              <option value="open">{t('document_workflow.task_status_open')}</option>
              <option value="resolved">{t('document_workflow.task_status_resolved')}</option>
              <option value="cancelled">{t('document_workflow.task_status_cancelled')}</option>
            </Form.Select>
          </div>
          <div className="col-auto">
            <Form.Label className="small mb-0">{t('document_workflow.workflow_name')}</Form.Label>
            <Form.Select
              size="sm"
              value={workflowFilter}
              onChange={(e) => {
                setWorkflowFilter(e.target.value === '' ? '' : Number(e.target.value))
                setPage(1)
              }}
              style={{ minWidth: 160 }}
            >
              <option value="">{t('document_workflow.filter_all')}</option>
              {workflowDefinitions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-auto">
            <Form.Label className="small mb-0">{t('document_workflow.document')}</Form.Label>
            <InputGroup size="sm" style={{ minWidth: 180 }}>
              <Form.Control
                placeholder={t('document_workflow.filter_document_placeholder')}
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
              <Button variant="outline-secondary" onClick={applyFilters} title={t('document_workflow.filter_apply')}>
                <i className="bi bi-search" />
              </Button>
            </InputGroup>
          </div>
          {hasActiveFilters && (
            <div className="col-auto">
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                {t('document_workflow.filter_clear')}
              </Button>
            </div>
          )}
          <div className="col-auto ms-sm-auto">
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-primary"
                size="sm"
                id="workflowTaskListColumnPicker"
                className="d-inline-flex align-items-center"
                aria-label={t('document_workflow.show_columns')}
              >
                <i className="bi bi-card-heading" aria-hidden />
                <span className="d-none d-sm-inline ms-1">{t('document_workflow.show_columns')}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow" align="end">
                <div className="px-3 py-1" style={{ minWidth: 220 }}>
                  {WORKFLOW_TASK_LIST_COLUMNS.map((id) => (
                    <div key={id} className="form-check my-1">
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        id={`workflowTaskCol-${id}`}
                        checked={colShow.has(id)}
                        onChange={() => toggleColumn(id)}
                      />
                      <label className="form-check-label" htmlFor={`workflowTaskCol-${id}`}>
                        {columnLabel(id)}
                      </label>
                    </div>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted">{t('document_workflow.my_workflow_tasks_empty')}</p>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <span className="text-muted small">{t('document_workflow.pagination_range', { from, to, count })}</span>
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 'auto' }}
              className="document-workflow-task-list__page-size"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} {t('document_workflow.per_page')}
                </option>
              ))}
            </Form.Select>
          </div>
          <AppTable
            responsive
            hover
            className="document-workflow-task-list__table"
            header={
              <thead>
                <tr>
                  <th style={{ width: '2rem' }} aria-label={t('document_workflow.expand_row')} />
                  {colShow.has('document') && <th>{columnLabel('document')}</th>}
                  {colShow.has('workflow') && <th>{columnLabel('workflow')}</th>}
                  {colShow.has('workflow_status') && <th>{columnLabel('workflow_status')}</th>}
                  {colShow.has('step') && <th>{columnLabel('step')}</th>}
                  {colShow.has('status') && <th>{columnLabel('status')}</th>}
                  {colShow.has('assigned') && <th>{columnLabel('assigned')}</th>}
                  {colShow.has('created') && <th>{columnLabel('created')}</th>}
                </tr>
              </thead>
            }
          >
            <tbody>
              {rows.map((row) => {
                const key = rowKey(row)
                const isExpanded = expandedKeys.has(key)
                const hasResolved = row.resolved_tasks.length > 0
                const firstOpen = row.open_tasks[0]
                const firstResolved = row.resolved_tasks[0]
                const summaryStep =
                  row.open_tasks.length > 0
                    ? row.open_tasks.map((o) => o.step_name || `#${o.step_id}`).join(', ')
                    : row.resolved_tasks.length > 0
                      ? row.resolved_tasks.map((r) => r.step_name || `#${r.step_id}`).join(', ')
                      : '—'
                const displayCreated = firstOpen?.created_at ?? firstResolved?.created_at ?? ''
                const displayAssigned =
                  firstOpen?.assigned_display?.trim() ||
                  firstResolved?.assigned_display?.trim() ||
                  firstOpen?.assigned_usernames?.join(', ') ||
                  firstResolved?.assigned_usernames?.join(', ') ||
                  ''

                return (
                  <React.Fragment key={key}>
                    <tr className={isExpanded ? 'document-workflow-task-list__row--expanded' : ''}>
                      <td className="document-workflow-task-list__expand">
                        {hasResolved ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 border-0"
                            onClick={() => toggleExpanded(key)}
                            aria-expanded={isExpanded}
                            title={isExpanded ? t('document_workflow.collapse_row') : t('document_workflow.expand_row')}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`} />
                          </button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {colShow.has('document') && (
                        <td>
                          <Link to={`/documents/${row.document_id}`}>
                            {(row.document_title || '').trim() || `#${row.document_id}`}
                          </Link>
                        </td>
                      )}
                      {colShow.has('workflow') && <td>{row.workflow_definition_name}</td>}
                      {colShow.has('workflow_status') && <td>{(row.workflow_status_name || '').trim() || '—'}</td>}
                      {colShow.has('step') && <td>{summaryStep}</td>}
                      {colShow.has('status') && (
                        <td>
                          <Badge bg={statusBadgeVariant(row.overall_status)}>
                            {t(statusLabelKey(row.overall_status))}
                          </Badge>
                        </td>
                      )}
                      {colShow.has('assigned') && (
                        <td className="document-workflow-task-list__assigned">{displayAssigned || '—'}</td>
                      )}
                      {colShow.has('created') && (
                        <td className="text-nowrap">{displayCreated ? formatDateTime(displayCreated) : '—'}</td>
                      )}
                    </tr>
                    {isExpanded && hasResolved && (
                      <tr className="document-workflow-task-list__sub-row">
                        <td colSpan={tableColSpan} className="p-0 bg-light">
                          <div className="p-2">
                            <table className="table table-sm table-bordered mb-0 document-workflow-task-list__subtable">
                              <thead>
                                <tr>
                                  <th>{t('document_workflow.step')}</th>
                                  <th>{t('document_workflow.task_status')}</th>
                                  <th>{t('document_workflow.action_status')}</th>
                                  <th>{t('document_workflow.comment')}</th>
                                  <th>{t('document_workflow.resolved_at')}</th>
                                  <th>{t('document_workflow.resolved_by')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.resolved_tasks.map((r, idx) => (
                                  <tr key={`${row.document_id}-${row.workflow_definition_id}-${r.step_id}-${idx}`}>
                                    <td>{r.step_name || `—`}</td>
                                    <td>
                                      <Badge bg={statusBadgeVariant(r.status)}>{t(statusLabelKey(r.status))}</Badge>
                                    </td>
                                    <td>{r.action_name || '—'}</td>
                                    <td className="document-workflow-task-list__comment">{(r.comment || '').trim() || '—'}</td>
                                    <td className="text-nowrap">{r.resolved_at ? formatDateTime(r.resolved_at) : '—'}</td>
                                    <td>{r.resolved_by_username || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </AppTable>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
            <span className="text-muted small">{t('document_workflow.page_of', { current: page, total: totalPages })}</span>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} showFirstLast />
          </div>
        </>
      )}
    </div>
  )
}
